/**
 * Ticket 70: "room disconnects on screen-off" — mobile Safari, device
 * auto-locks while listening and all sound stops until the screen returns.
 *
 * nofilter has no native client, so the answers a native app would reach for
 * (an Android foreground service, CallKit on iOS) do not exist here. What the
 * web platform does offer is this pair, and between them they cover the
 * reported case:
 *
 *   1. **Do not let the screen switch off.** The repro is an auto-lock, not a
 *      deliberate press of the power button, so holding a screen wake lock for
 *      the duration of the room removes the trigger outright.
 *   2. **Put the audio back together on return.** For everything the lock
 *      cannot prevent — a deliberate lock, a switch to another app, a browser
 *      too old for Wake Lock — recovery has to be automatic.
 *
 * Both halves are pure functions over injected dependencies, so the awkward
 * cases (the OS revoking a lock, Low Power Mode refusing one, autoplay
 * refusing a replay) are unit-testable without a device.
 *
 * ## The hard limit, stated plainly
 *
 * None of this survives a deliberate screen lock or an app switch on iOS:
 * Safari suspends WebRTC when the page is not frontmost and no web API
 * overrides that. Recovery on return is the honest ceiling there.
 */

// ── screen wake lock ──────────────────────────────────────────────────────

/**
 * The slice of a WakeLockSentinel this needs. Structural, so tests do not need
 * a real one (jsdom has no Wake Lock API).
 */
export interface WakeLockSentinelLike {
  released?: boolean;
  release: () => Promise<void>;
  addEventListener: (type: string, listener: () => void) => void;
}

export interface WakeLockDeps {
  /**
   * Null when the browser has no Wake Lock API — iOS below 16.4 and older
   * Android browsers. Every method then becomes a no-op.
   */
  requestWakeLock: (() => Promise<WakeLockSentinelLike>) | null;
  isPageVisible: () => boolean;
  log?: (message: string) => void;
}

export interface WakeLockController {
  /** Take the lock, if the page is visible and the browser allows it. */
  acquire: () => Promise<void>;
  /** Call on every visibilitychange. Re-takes the lock the OS revoked. */
  syncToVisibility: () => Promise<void>;
  /** Leaving the room: drop the lock and stop re-taking it. */
  release: () => Promise<void>;
  isHeld: () => boolean;
  isSupported: () => boolean;
}

/**
 * Feature-detects the Wake Lock API.
 *
 * Returns null rather than throwing so the caller's shape is the same on every
 * browser: an unsupported engine simply gets an inert controller.
 */
export const wakeLockRequester = (
  nav: Navigator = navigator,
): (() => Promise<WakeLockSentinelLike>) | null => {
  // Read through `unknown`: TypeScript's DOM lib declares `navigator.wakeLock`
  // as always present, which is exactly the assumption this function exists to
  // check at runtime.
  const wakeLock = (
    nav as unknown as {
      wakeLock?: { request?: (type: string) => Promise<WakeLockSentinelLike> };
    }
  ).wakeLock;
  const request = wakeLock?.request;
  if (!request) return null;
  return () => request.call(wakeLock, 'screen');
};

export const createWakeLockController = (
  deps: WakeLockDeps,
): WakeLockController => {
  let sentinel: WakeLockSentinelLike | null = null;
  // Distinct from "do we hold one right now": once the room is left, a later
  // visibilitychange must NOT quietly take a fresh lock and keep the screen
  // awake for a room the user has walked out of.
  let wanted = false;
  // A second acquire() landing while the first is still in flight would leave
  // an orphan sentinel that nothing ever releases.
  let inFlight: Promise<void> | null = null;

  const isSupported = () => deps.requestWakeLock !== null;

  const take = async (): Promise<void> => {
    const request = deps.requestWakeLock;
    if (!request) return;
    if (sentinel && !sentinel.released) return;
    // A hidden page is refused by every implementation; asking anyway only
    // produces a rejection to swallow.
    if (!deps.isPageVisible()) return;

    try {
      const next = await request();
      // Released while we were awaiting, or the room was left mid-request.
      if (!wanted) {
        void next.release().catch(() => undefined);
        return;
      }
      sentinel = next;
      next.addEventListener('release', () => {
        deps.log?.('[Room] WAKE_LOCK: released by the system');
      });
      deps.log?.('[Room] WAKE_LOCK: screen lock held for the room');
    } catch (err: unknown) {
      // Low Power Mode and a backgrounded tab both reject. Not being able to
      // keep the screen awake is a degraded room, never a broken one.
      deps.log?.(`[Room] WAKE_LOCK: refused (${String(err)})`);
    }
  };

  const runExclusive = async (): Promise<void> => {
    if (inFlight) return inFlight;
    inFlight = take().finally(() => {
      inFlight = null;
    });
    return inFlight;
  };

  return {
    isSupported,
    isHeld: () => sentinel !== null && !sentinel.released,

    acquire: async () => {
      wanted = true;
      await runExclusive();
    },

    syncToVisibility: async () => {
      if (!wanted) return;
      if (!deps.isPageVisible()) return;
      await runExclusive();
    },

    release: async () => {
      wanted = false;
      const held = sentinel;
      sentinel = null;
      if (!held || held.released) return;
      await held.release().catch(() => undefined);
    },
  };
};

// ── foreground audio recovery ─────────────────────────────────────────────

/** The slice of an AudioContext this needs. */
export interface ResumableContext {
  state: string;
  resume: () => Promise<void>;
}

/** The slice of an HTMLAudioElement this needs. */
export interface ReplayableElement {
  paused: boolean;
  muted: boolean;
  play: () => Promise<void>;
}

export interface RecoveryDeps {
  contexts: () => ResumableContext[];
  elements: () => ReplayableElement[];
  /** LiveKit's `room.startAudio()`, the existing blocked-playback recovery. */
  startAudio?: (() => Promise<void>) | null;
  log?: (message: string) => void;
}

export interface RecoveryReport {
  contextsResumed: number;
  elementsReplayed: number;
  startAudioCalled: boolean;
}

/**
 * Called when the page becomes visible again.
 *
 * Deliberately does NOT touch `muted`. That bit already has two owners in
 * `useRoomAudio` — the host's own OBS ingest (virtual feedback) and the echo
 * guard during a screen share — and a third writer here would un-mute a stream
 * those two silenced on purpose. Recovery restarts playback; it does not
 * decide what should be audible.
 */
export const recoverBackgroundedAudio = async (
  deps: RecoveryDeps,
): Promise<RecoveryReport> => {
  let contextsResumed = 0;
  let elementsReplayed = 0;
  let blocked = 0;

  // allSettled throughout: one refusal must not strand the rest. A viewer
  // hearing three of four speakers is a bug; hearing none because the first
  // element threw is the bug we are fixing.
  const contexts = deps.contexts().filter((ctx) => ctx.state === 'suspended');
  await Promise.allSettled(
    contexts.map(async (ctx) => {
      await ctx.resume();
      contextsResumed += 1;
    }),
  );

  const paused = deps.elements().filter((el) => el.paused);
  await Promise.allSettled(
    paused.map(async (el) => {
      try {
        await el.play();
        elementsReplayed += 1;
      } catch {
        blocked += 1;
      }
    }),
  );

  let startAudioCalled = false;
  if (blocked > 0 && deps.startAudio) {
    // Autoplay policy refused the silent replay. startAudio() replays every
    // attached element at once; outside a user gesture it may refuse too, in
    // which case the existing "Tap to enable audio" button still covers it.
    try {
      await deps.startAudio();
      startAudioCalled = true;
    } catch {
      deps.log?.('[Room] BACKGROUND_RECOVERY: startAudio() refused');
    }
  }

  if (contextsResumed || elementsReplayed || blocked) {
    deps.log?.(
      `[Room] BACKGROUND_RECOVERY: contexts=${contextsResumed} elements=${elementsReplayed} blocked=${blocked}`,
    );
  }

  return { contextsResumed, elementsReplayed, startAudioCalled };
};

/**
 * The room's playback elements, by the id convention `useRoomAudio` gives
 * them on attach (`lk-audio-<identity>` and `lk-audio-<identity>-screen`).
 * Scoped by prefix so an unrelated `<audio>` on the page is never touched.
 */
export const collectRoomAudioElements = (
  doc: Document = document,
): HTMLAudioElement[] =>
  Array.from(doc.querySelectorAll<HTMLAudioElement>('audio[id^="lk-audio-"]'));

// ── microphone restoration ────────────────────────────────────────────────

export interface MicRestoreState {
  /** Host, co-host or speaker. Listeners have no mic to restore. */
  canPublish: boolean;
  /** The user muted themselves. Their choice outranks any recovery. */
  userMuted: boolean;
  /** A moderator muted them. Recovery must never route around that. */
  forceMuted: boolean;
  /**
   * The published track is muted or gone — what the OS does to a backgrounded
   * page's capture.
   */
  trackMuted: boolean;
}

/**
 * Whether returning to the foreground should re-enable the microphone.
 *
 * The reason this is a named rule rather than an `if` buried in the hook: the
 * failure mode is a live mic on someone who believes they are muted. Every
 * intentional reason for silence — self-mute, moderator force-mute, being a
 * listener — has to beat "the OS interrupted our capture", and each of those
 * gets its own test.
 */
export const shouldRestoreMicrophone = (state: MicRestoreState): boolean =>
  state.canPublish && !state.userMuted && !state.forceMuted && state.trackMuted;
